import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/Icon';
import ImageLightbox from '../../components/ImageLightbox';
import MessageBubble from './MessageBubble';
import MessageComposer from './MessageComposer';
import ParticipantsPanel from './ParticipantsPanel';
import PeoplePickerDialog from './PeoplePickerDialog';
import { useChat } from '../ChatContext';
import { ONLINE_WINDOW_MS } from '../chatConstants';
import {
  canManageGroup, chatInitials, chatTitle, dayKey, findParticipant, formatDayLabel, initialsOf,
  isGroupChat, myParticipantRole, newestMessageId, participantName, systemMessageText,
} from '../chatUtils';
import { GROUP_TITLE_MAX_LENGTH } from '../chatConstants';

const senderAvatar = (chat, senderId) => findParticipant(chat, senderId)?.avatar || null;

const ChatWindow = ({ chat, locale }) => {
  const { t } = useTranslation();
  const {
    messagesByChat, hasMoreByChat, typingByChat, activityByUser, peerReadByChat,
    currentUserId, connected,
    loadOlder, sendMessage, toggleReaction, markRead, sendTyping, togglePinned, toggleMuted,
    addMembers, removeMember, renameChat, leaveChat,
  } = useChat();

  const [replyTo, setReplyTo] = useState(null);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [windowFocused, setWindowFocused] = useState(() => !document.hidden);

  // Вкладка свёрнута — сообщения не считаются прочитанными.
  useEffect(() => {
    const onVisibility = () => setWindowFocused(!document.hidden);
    const onFocus = () => setWindowFocused(true);
    const onBlur = () => setWindowFocused(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const stickToBottomRef = useRef(true);

  const chatId = chat?.id;
  const group = isGroupChat(chat);
  const myRole = myParticipantRole(chat, currentUserId);
  const canManage = group && canManageGroup(chat, currentUserId);
  const messages = useMemo(() => messagesByChat[chatId] || [], [messagesByChat, chatId]);
  const typingUsers = Object.keys(typingByChat[chatId] || {});

  // Онлайн определяется активностью собеседника — отдельного эндпоинта нет.
  const peerActive =
    chat?.peerId && Date.now() - (activityByUser[chat.peerId] || 0) < ONLINE_WINDOW_MS;

  // Автоскролл вниз, пока пользователь не ушёл читать историю.
  useEffect(() => {
    if (stickToBottomRef.current) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [messages.length, chatId]);

  useEffect(() => {
    setReplyTo(null);
    stickToBottomRef.current = true;
  }, [chatId]);

  // Прочитано: только когда чат открыт и окно в фокусе. Повторную отправку
  // одного и того же курсора гасит markRead (двигается только вперёд).
  useEffect(() => {
    if (!chatId || !windowFocused) return;
    const lastId = newestMessageId(messages);
    if (lastId != null) markRead(chatId, lastId);
  }, [chatId, messages, markRead, windowFocused]);

  const handleScroll = useCallback(async (event) => {
    const el = event.currentTarget;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;

    if (el.scrollTop < 80 && hasMoreByChat[chatId] && !loadingOlder) {
      setLoadingOlder(true);
      const previousHeight = el.scrollHeight;
      try {
        await loadOlder(chatId);
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight - previousHeight;
        });
      } finally {
        setLoadingOlder(false);
      }
    }
  }, [chatId, hasMoreByChat, loadOlder, loadingOlder]);

  const handleSend = useCallback(
    (payload) => sendMessage({ chatId, ...payload }),
    [chatId, sendMessage]
  );

  const submitRename = useCallback(async (event) => {
    event.preventDefault();
    const next = titleDraft.trim();
    setRenaming(false);
    if (!next || next === chat?.title) return;
    try {
      await renameChat(chatId, next);
    } catch {
      /* состав и название перечитываются из /api/chats, показывать нечего */
    }
  }, [chat?.title, chatId, renameChat, titleDraft]);

  const handleJumpToReply = useCallback((messageId) => {
    const node = document.querySelector(`[data-message-id="${messageId}"]`);
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      node.classList.add('chat-msg-highlight');
      setTimeout(() => node.classList.remove('chat-msg-highlight'), 1200);
    }
  }, []);

  if (!chat) {
    return (
      <section className="chat-window chat-window--empty">
        <Icon name="message-square" size={40} />
        <p>{t('chat.window.empty')}</p>
      </section>
    );
  }

  // Разделители по датам + группировка подряд идущих сообщений одного отправителя.
  const rendered = [];
  let previousDay = null;
  let previousSender = null;

  messages.forEach((message, index) => {
    const currentDay = dayKey(message.createdAt);
    if (currentDay !== previousDay) {
      rendered.push(
        <div className="chat-day" key={`day-${currentDay}-${index}`}>
          <span>{formatDayLabel(message.createdAt, locale)}</span>
        </div>
      );
      previousSender = null;
    }
    previousDay = currentDay;

    // Системные события — до всякой отрисовки пузыря: без аватара, времени,
    // реакций и статуса, на них нельзя ответить.
    if (message.messageType === 'SYSTEM') {
      previousSender = null;
      rendered.push(
        <div
          className="chat-system"
          key={message.id ?? message.tempId}
          data-message-id={message.id ?? ''}
        >
          <span>{systemMessageText(message, chat)}</span>
        </div>
      );
      return;
    }

    const own = String(message.senderId) === String(currentUserId);
    const startsGroup = previousSender !== message.senderId;
    previousSender = message.senderId;

    rendered.push(
      <div
        key={message.id ?? message.tempId}
        data-message-id={message.id ?? ''}
        className={`chat-msg-slot${own ? ' own' : ''}${startsGroup ? ' group-start' : ''}`}
      >
        {/* Имя отправителя — только у первого сообщения в серии; в личных чатах не нужно */}
        {!own && startsGroup && (
          <div className="chat-msg-author">
            {senderAvatar(chat, message.senderId) ? (
              <img
                className="chat-avatar chat-avatar--sm chat-avatar--img"
                src={senderAvatar(chat, message.senderId)}
                alt=""
              />
            ) : (
              <span className="chat-avatar chat-avatar--sm">
                {group ? initialsOf(participantName(chat, message.senderId)) : chatInitials(chat)}
              </span>
            )}
            <span className="chat-msg-author-name">
              {group ? participantName(chat, message.senderId) : chatTitle(chat)}
            </span>
          </div>
        )}
        <MessageBubble
          message={message}
          own={own}
          showTail={startsGroup}
          currentUserId={currentUserId}
          peerReadCursor={peerReadByChat[chatId] ?? 0}
          locale={locale}
          onReply={setReplyTo}
          onReact={(msg, emoji) => toggleReaction(chatId, msg.id, emoji)}
          onOpenImage={setLightboxUrl}
          onJumpToReply={handleJumpToReply}
        />
      </div>
    );
  });

  return (
    <section className="chat-window">
      <header className="chat-window-head">
        <span className={`chat-avatar${group ? ' chat-avatar--group' : ''}`}>{chatInitials(chat)}</span>
        <div className="chat-window-titles">
          {renaming ? (
            <form className="chat-rename" onSubmit={submitRename}>
              <input
                autoFocus
                className="chat-modal-input"
                value={titleDraft}
                maxLength={GROUP_TITLE_MAX_LENGTH}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={() => setRenaming(false)}
              />
              <button type="submit" className="chat-icon-btn" title={t('chat.window.save')}>
                <Icon name="check" size={16} />
              </button>
            </form>
          ) : (
            <span
              className={`chat-window-name${canManage ? ' editable' : ''}`}
              onClick={() => {
                if (!canManage) return;
                setTitleDraft(chat.title || '');
                setRenaming(true);
              }}
              title={canManage ? t('chat.window.rename') : undefined}
            >
              {chatTitle(chat)}
            </span>
          )}
          {group ? (
            <span className="chat-window-status">
              {typingUsers.length > 0
                ? t('chat.window.typingNamed', { name: participantName(chat, typingUsers[0]) })
                : t('chat.window.members', { count: (chat.participants || []).length })}
            </span>
          ) : typingUsers.length > 0 ? (
            <span className="chat-window-status typing">{t('chat.window.typing')}</span>
          ) : peerActive ? (
            <span className="chat-window-status online">{t('chat.window.activeNow')}</span>
          ) : null}
        </div>

        <div className="chat-window-actions">
          {!connected && (
            <span className="chat-offline" title={t('chat.window.offlineHint')}>
              <Icon name="slash" size={14} />
              {t('chat.window.offline')}
            </span>
          )}
          {group && (
            <button
              type="button"
              className={`chat-members-btn${participantsOpen ? ' active' : ''}`}
              onClick={() => setParticipantsOpen((v) => !v)}
              title={t('chat.window.participants')}
            >
              <Icon name="users" size={18} />
              {(chat.participants || []).length}
            </button>
          )}
          <div className="chat-menu-wrap">
            <button
              type="button"
              className="chat-icon-btn"
              onClick={() => setMenuOpen((v) => !v)}
              title={t('chat.window.actions')}
            >
              <Icon name="sliders" size={18} />
            </button>
            {menuOpen && (
              <div className="chat-menu">
                <button
                  type="button"
                  onClick={() => {
                    togglePinned(chat.id, !chat.pinned);
                    setMenuOpen(false);
                  }}
                >
                  <Icon name="tag" size={14} />
                  {chat.pinned ? t('chat.window.unpin') : t('chat.window.pin')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    toggleMuted(chat.id, !chat.muted);
                    setMenuOpen(false);
                  }}
                >
                  <Icon name="slash" size={14} />
                  {chat.muted ? t('chat.window.unmute') : t('chat.window.mute')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="chat-window-body">
      <div className="chat-window-main">
      <div className="chat-scroll" ref={scrollRef} onScroll={handleScroll}>
        {loadingOlder && (
          <div className="chat-loading-older">
            <div className="strict-loader" />
          </div>
        )}
        {rendered}
        {typingUsers.length > 0 && (
          <div className="chat-typing">
            <span />
            <span />
            <span />
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <MessageComposer
        chatId={chatId}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        onSend={handleSend}
        onTyping={() => sendTyping(chatId)}
        placeholderName={chatTitle(chat)}
      />
      </div>

      {group && participantsOpen && (
        <ParticipantsPanel
          chat={chat}
          currentUserId={currentUserId}
          canManage={canManage}
          myRole={myRole}
          onClose={() => setParticipantsOpen(false)}
          onAddMembers={() => setAddMembersOpen(true)}
          onRemoveMember={(userId) => removeMember(chatId, userId)}
          onLeave={() => leaveChat(chatId)}
        />
      )}
      </div>

      <PeoplePickerDialog
        open={addMembersOpen}
        mode="add"
        excludeIds={(chat.participants || []).map((p) => p.userId)}
        onClose={() => setAddMembersOpen(false)}
        onSubmit={({ memberIds }) => addMembers(chatId, memberIds)}
      />

      {lightboxUrl && (
        <ImageLightbox
          images={[{ url: lightboxUrl }]}
          initialIndex={0}
          onClose={() => setLightboxUrl(null)}
        />
      )}
    </section>
  );
};

export default ChatWindow;
